export interface AdaptRuntimeConfig {
  apiBaseUrl: string;
  apiVersion: string;
  requestTimeoutMs: number;
  multipartRequestTimeoutMs: number;
  keycloakUrl: string;
  keycloakRealm: string;
  keycloakClientId: string;
  /** Optional IdP hint passed to Keycloak on login. Set to 'microsoft-entra'
   * for Azure deployments to skip the Keycloak login form. Leave empty for
   * local dev to show the Keycloak login form directly. */
  keycloakIdpHint: string;
  environment: string;
  applicationName: string;
  appBasePath: string;
  loginRedirectPath: string;
  logoutRedirectPath: string;
}

export type RuntimeConfigInput = Partial<AdaptRuntimeConfig> &
  Record<string, unknown>;
export type RuntimeConfigEnv = Record<
  string,
  string | boolean | number | undefined
>;
export type RuntimeConfigFetcher = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface LoadRuntimeConfigOptions {
  configPath?: string;
  defaults?: Partial<AdaptRuntimeConfig>;
  env?: RuntimeConfigEnv;
  fetcher?: RuntimeConfigFetcher;
  forceReload?: boolean;
  skipRuntimeFetch?: boolean;
}

const DEFAULT_CONFIG_PATH = "/config.json";

export const DEFAULT_RUNTIME_CONFIG: AdaptRuntimeConfig = {
  apiBaseUrl: "/api/",
  apiVersion: "1.0",
  requestTimeoutMs: 10_000,
  multipartRequestTimeoutMs: 60_000,
  keycloakUrl: "http://localhost:28080",
  keycloakRealm: "adapt-dev",
  keycloakClientId: "adapt-ui",
  keycloakIdpHint: "",
  environment: "development",
  applicationName: "ADAPT",
  appBasePath: "/",
  loginRedirectPath: "/",
  logoutRedirectPath: "/",
};

let runtimeConfig: AdaptRuntimeConfig | null = null;

const asString = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return undefined;
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const envValue = (
  env: RuntimeConfigEnv,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = asString(env[key]);
    if (value) return value;
  }
  return undefined;
};

const envNumber = (
  env: RuntimeConfigEnv,
  ...keys: string[]
): number | undefined => {
  for (const key of keys) {
    const value = asNumber(env[key]);
    if (value !== undefined) return value;
  }
  return undefined;
};

const trimPath = (path: string): string => {
  if (path === "") return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const importMetaEnv = (): RuntimeConfigEnv => {
  const meta = import.meta as ImportMeta & { env?: RuntimeConfigEnv };
  return meta.env ?? {};
};

export const configFromEnv = (env: RuntimeConfigEnv = importMetaEnv()): Partial<AdaptRuntimeConfig> => ({
  apiBaseUrl:               envValue(env, "VITE_ADAPT_API_BASE_URL", "VITE_UI_API_URL", "ADAPT_API_BASE_URL"),
  apiVersion:               envValue(env, "VITE_ADAPT_API_VERSION", "VITE_API_VERSION", "ADAPT_API_VERSION"),
  requestTimeoutMs:         envNumber(env, "VITE_ADAPT_API_TIMEOUT_MS", "VITE_API_TIMEOUT", "ADAPT_API_TIMEOUT_MS"),
  multipartRequestTimeoutMs: envNumber(env, "VITE_ADAPT_API_MULTIPART_TIMEOUT_MS", "VITE_API_TIMEOUT_MULTIPART", "ADAPT_API_MULTIPART_TIMEOUT_MS"),
  keycloakUrl:              envValue(env, "VITE_ADAPT_KEYCLOAK_URL", "VITE_KEYCLOAK_URL", "KEYCLOAK_URL"),
  keycloakRealm:            envValue(env, "VITE_ADAPT_KEYCLOAK_REALM", "VITE_KEYCLOAK_REALM", "KEYCLOAK_REALM"),
  keycloakClientId:         envValue(env, "VITE_ADAPT_KEYCLOAK_CLIENT_ID", "VITE_KEYCLOAK_CLIENT_ID", "KEYCLOAK_CLIENT_ID"),
  environment:              envValue(env, "VITE_ADAPT_ENVIRONMENT", "VITE_ENVIRONMENT", "NODE_ENV"),
  applicationName:          envValue(env, "VITE_ADAPT_APPLICATION_NAME", "VITE_APPLICATION_NAME"),
  appBasePath:              envValue(env, "VITE_ADAPT_APP_BASE_PATH", "BASE_URL"),
  loginRedirectPath:        envValue(env, "VITE_ADAPT_LOGIN_REDIRECT_PATH"),
  logoutRedirectPath:       envValue(env, "VITE_ADAPT_LOGOUT_REDIRECT_PATH"),
});

function makeSourceValue(
  input: RuntimeConfigInput,
  fallback: RuntimeConfigInput,
): (...keys: string[]) => unknown {
  return (...keys: string[]): unknown => {
    for (const key of keys) {
      const value = input[key];
      if (value !== undefined && value !== null) return value;
    }
    for (const key of keys) {
      const value = fallback[key];
      if (value !== undefined && value !== null) return value;
    }
    return undefined;
  };
}

export const normaliseRuntimeConfig = (
  input: RuntimeConfigInput = {},
  defaults: Partial<AdaptRuntimeConfig> = {},
): AdaptRuntimeConfig => {
  const sv = makeSourceValue(input, defaults as RuntimeConfigInput);
  return {
    apiBaseUrl:               asString(sv("apiBaseUrl", "apiUrl"))                             ?? DEFAULT_RUNTIME_CONFIG.apiBaseUrl,
    apiVersion:               asString(sv("apiVersion"))                                        ?? DEFAULT_RUNTIME_CONFIG.apiVersion,
    requestTimeoutMs:         asNumber(sv("requestTimeoutMs", "apiTimeout"))                   ?? DEFAULT_RUNTIME_CONFIG.requestTimeoutMs,
    multipartRequestTimeoutMs: asNumber(sv("multipartRequestTimeoutMs", "apiTimeoutMultipart")) ?? DEFAULT_RUNTIME_CONFIG.multipartRequestTimeoutMs,
    keycloakUrl:              asString(sv("keycloakUrl"))                                       ?? DEFAULT_RUNTIME_CONFIG.keycloakUrl,
    keycloakRealm:            asString(sv("keycloakRealm"))                                     ?? DEFAULT_RUNTIME_CONFIG.keycloakRealm,
    keycloakClientId:         asString(sv("keycloakClientId"))                                  ?? DEFAULT_RUNTIME_CONFIG.keycloakClientId,
    keycloakIdpHint:          asString(sv("keycloakIdpHint"))                                   ?? DEFAULT_RUNTIME_CONFIG.keycloakIdpHint,
    environment:              asString(sv("environment"))                                        ?? DEFAULT_RUNTIME_CONFIG.environment,
    applicationName:          asString(sv("applicationName", "applicationId", "appName"))       ?? DEFAULT_RUNTIME_CONFIG.applicationName,
    appBasePath:              trimPath(asString(sv("appBasePath"))              ?? DEFAULT_RUNTIME_CONFIG.appBasePath),
    loginRedirectPath:        trimPath(asString(sv("loginRedirectPath"))        ?? DEFAULT_RUNTIME_CONFIG.loginRedirectPath),
    logoutRedirectPath:       trimPath(asString(sv("logoutRedirectPath"))       ?? DEFAULT_RUNTIME_CONFIG.logoutRedirectPath),
  };
};

const shouldFetchRuntimeConfig = (
  env: RuntimeConfigEnv,
  skipRuntimeFetch: boolean | undefined,
): boolean => {
  if (skipRuntimeFetch) return false;
  return env.DEV !== true && env.MODE !== "test";
};

const runtimeFetcher = (
  fetcher: LoadRuntimeConfigOptions["fetcher"],
): RuntimeConfigFetcher | undefined => {
  if (typeof fetcher === "function") return fetcher;
  if (typeof fetch === "function") return fetch;
  return undefined;
};

const fetchRuntimeConfig = async (
  configPath: string,
  fetcher: RuntimeConfigFetcher,
): Promise<RuntimeConfigInput | undefined> => {
  const response = await fetcher(configPath, {
    headers: { Accept: "application/json" },
  });
  const contentType = response.headers.get("content-type") ?? "";

  if (!response.ok || !contentType.includes("application/json")) {
    return undefined;
  }

  return (await response.json()) as RuntimeConfigInput;
};

export const loadRuntimeConfig = async (
  options: LoadRuntimeConfigOptions = {},
): Promise<AdaptRuntimeConfig> => {
  if (runtimeConfig && !options.forceReload) return runtimeConfig;

  const env = options.env ?? importMetaEnv();
  let fileConfig: RuntimeConfigInput | undefined;
  const fetcher = runtimeFetcher(options.fetcher);

  if (fetcher && shouldFetchRuntimeConfig(env, options.skipRuntimeFetch)) {
    try {
      fileConfig = await fetchRuntimeConfig(
        options.configPath ?? DEFAULT_CONFIG_PATH,
        fetcher,
      );
    } catch {
      fileConfig = undefined;
    }
  }

  runtimeConfig = normaliseRuntimeConfig(
    {
      ...configFromEnv(env),
      ...fileConfig,
    },
    options.defaults,
  );
  return runtimeConfig;
};

export const setRuntimeConfig = (
  config: RuntimeConfigInput,
): AdaptRuntimeConfig => {
  runtimeConfig = normaliseRuntimeConfig(config);
  return runtimeConfig;
};

export const getRuntimeConfig = (): AdaptRuntimeConfig => {
  if (!runtimeConfig) {
    throw new Error(
      "ADAPT runtime configuration is not loaded. Call loadRuntimeConfig() before using it.",
    );
  }
  return runtimeConfig;
};

export const isRuntimeConfigLoaded = (): boolean => runtimeConfig !== null;

export const resetRuntimeConfigForTests = (): void => {
  runtimeConfig = null;
};
